import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import StickyAuditCTA from "@/components/StickyAuditCTA";
import HomePage from "@/pages/HomePage";
import IndustryPage from "@/pages/IndustryPage";
import LoginPage from "@/pages/LoginPage";
import PortalPage from "@/pages/PortalPage";
import AuthCallback from "@/pages/AuthCallback";
import AffiliatePage from "@/pages/AffiliatePage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import { installCalendlyTracking } from "@/lib/analytics";

function ScrollToHash() {
  const { hash, pathname } = useLocation();
  React.useEffect(() => {
    if (hash.includes("industry=")) return; // Pricing handles industry deep links itself
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.slice(1).split("?")[0]);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [hash, pathname]);
  return null;
}

function AppShell() {
  const [user, setUser] = React.useState(null);
  const location = useLocation();

  React.useEffect(() => {
    // Mount-only: hydrate the current user once on first paint.
    // If returning from OAuth callback, skip /me check.
    if (window.location.hash?.includes('session_id=')) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        // Anonymous visitor — leave user as null.
        void err;
      }
    })();
  }, []);

  React.useEffect(() => installCalendlyTracking(), []);

  // OAuth callback handler — synchronous check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Private admin area: rendered apart from the public site chrome.
  if (location.pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin/analytics" element={<AdminAnalyticsPage/>}/>
      </Routes>
    );
  }

  return (
    <>
      <ScrollToHash/>
      <Nav user={user}/>
      <Routes>
        <Route path="/" element={<HomePage/>}/>
        <Route path="/industries/:slug" element={<IndustryPage/>}/>
        <Route path="/login" element={<LoginPage/>}/>
        <Route path="/portal" element={<PortalPage/>}/>
        <Route path="/affiliate" element={<AffiliatePage/>}/>
        <Route path="/checkout/success" element={<CheckoutSuccess/>}/>
      </Routes>
      <Footer/>
      <StickyAuditCTA/>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell/>
    </BrowserRouter>
  );
}
